import { ApolloCache, useMutation } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { ActivateAction } from 'components/ActivateAction'
import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { PasswordIcon, PencilIcon, SwitchIcon, WarningIcon } from 'components/Icons'
import { ACTIVATE_BUSINESS_CLIENT_USER, DEACTIVATE_BUSINESS_CLIENT_USER } from 'features/BusinessClients/mutations'
import { REQUEST_RESET_USER_ACCOUNT_PASSWORD } from 'features/Drivers/mutations-legacy'
import { SuccessResetPasswordRequestModal } from 'features/UserAccount/components/SuccessResetPasswordRequestModal'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { canWrite } from 'features/Users/utils'
import { BusinessClientUser } from 'gql/generated/graphql'

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
  fill: rgb(245, 46, 46);
`

interface IBusinessClientUsersActionsProps {
  businessClientUser: BusinessClientUser
}

export const BusinessClientUsersActions = ({ businessClientUser }: IBusinessClientUsersActionsProps): ReactElement => {
  const login = useLogin()
  const { policies } = login.accessToken
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isShowingResetPasswordConfirmationModal, setIsShowingResetPasswordConfirmationModal] = useState(false)

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const updateStatus = (cache: ApolloCache<unknown>, isActive: boolean): void => {
    cache.modify({
      id: cache.identify({
        __typename: 'BusinessClientUser',
        id: businessClientUser.id
      }),
      fields: {
        isActive: () => isActive
      }
    })
  }

  const [activateBusinessClientUser, { loading: enableLoading, error: enableError }] = useMutation(
    ACTIVATE_BUSINESS_CLIENT_USER,
    {
      update: (cache): void => {
        updateStatus(cache, true)
      },
      onError: (): void => {
        // Do nothing
      }
    }
  )
  const [deactivateBusinessClientUser, { loading: disableLoading, error: disableError }] = useMutation(
    DEACTIVATE_BUSINESS_CLIENT_USER,
    {
      update: (cache): void => {
        updateStatus(cache, false)
      },
      onError: (): void => {
        // Do nothing
      }
    }
  )
  const toggleActivation = (): void => {
    if (!businessClientUser.isActive) {
      activateBusinessClientUser({
        variables: {
          input: {
            id: businessClientUser.id
          }
        }
      })
    } else {
      deactivateBusinessClientUser({
        variables: {
          input: {
            id: businessClientUser.id
          }
        }
      })
    }
  }
  const [requestResetUserAccountPassword, { error: resetPasswordRequestError, loading: resetPasswordRequestLoading }] =
    useMutation(REQUEST_RESET_USER_ACCOUNT_PASSWORD, {
      onCompleted: (): void => {
        setIsShowingResetPasswordConfirmationModal(true)
      }
    })
  const resetPassword = (): void => {
    requestResetUserAccountPassword({
      variables: {
        input: { email: businessClientUser.userAccount.email }
      }
    })
  }

  const canChangeStatus = canWrite(FEATURES.BUSINESS_CLIENT_USER, policies)
  const canRequestResetPassword = canWrite(FEATURES.BUSINESS_CLIENT_USER, policies)

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): TSFixMe => setOpenDropdown(false)}>
        <IconButton icon={<DotsIcon />} noBorder smallPadding onClick={toggleDropdown} />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.ItemLink
              href={`/business-clients/${businessClientUser.businessClient.id}/users/${businessClientUser.id}`}
            >
              <List.Icon>
                <PencilIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.edit" defaultMessage="Edit" />
            </List.ItemLink>
            <List.Item
              action
              onClick={(): void => {
                if (canChangeStatus) toggleActivation()
              }}
              loading={enableLoading || disableLoading}
              disabled={!canChangeStatus}
            >
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <ActivateAction isActive={businessClientUser.isActive} error={enableError || disableError} />
            </List.Item>
            <List.Item
              action
              loading={resetPasswordRequestLoading}
              onClick={(): void => {
                if (canRequestResetPassword) {
                  resetPassword()
                }
              }}
              disabled={!canRequestResetPassword}
            >
              <List.Icon>
                <PasswordIcon />
              </List.Icon>
              <FormattedMessage id="driver.resetPassword" defaultMessage="Reset password" />
              {resetPasswordRequestError && <StyledWarningIcon />}
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isShowingResetPasswordConfirmationModal && (
        <SuccessResetPasswordRequestModal
          userAccountEmail={businessClientUser.userAccount.email as string}
          onClose={(): void => setIsShowingResetPasswordConfirmationModal(false)}
        />
      )}
    </>
  )
}
